import classNames from 'classnames';
import { memo, useCallback, useId } from 'react';

import { Web } from '@air/next-icons';
import { Checkbox } from '@air/primitive-checkbox';
import { Tooltip } from '@air/primitive-tooltip';

export type InviteToLibrariesStepLibraryRowProps = {
  id: string;
  title: string;
  colorHex: string;
  icon: string;
  isSelected: boolean;
  disabled?: boolean;
  roleName?: string;
  onToggle: (id: string) => void;
};

export const InviteToLibrariesStepLibraryRow = memo(
  ({ id, title, colorHex, icon, isSelected, disabled, roleName, onToggle }: InviteToLibrariesStepLibraryRowProps) => {
    const labelId = useId();

    const handleCheckedChange = useCallback(() => {
      onToggle(id);
    }, [id, onToggle]);

    return (
      <label
        className={classNames('flex items-center justify-between gap-2 rounded-4 px-3 py-2.5 transition-colors', {
          'cursor-default': disabled,
          'cursor-pointer': !disabled,
          'hover:bg-grey-3': !disabled && !isSelected,
          'bg-blue-2': isSelected && !disabled,
        })}
        htmlFor={labelId}
      >
        <div className="flex min-w-0 items-center gap-2">
          <div
            className="flex size-10 shrink-0 items-center justify-center rounded-8 bg-grey-4 text-20"
            style={{ backgroundColor: colorHex }}
          >
            {icon}
          </div>
          <div className="flex min-w-0 flex-col">
            <div className="flex items-center gap-1">
              <span className="truncate text-14 text-grey-12">{title}</span>
              {disabled && (
                <Tooltip label="Workspace users are automatically members of this library" side="top" align="center">
                  <Web className="size-4 shrink-0 fill-grey-8" />
                </Tooltip>
              )}
            </div>
            {roleName && (
              <Tooltip
                label="User's library role can be adjusted from the default after invite"
                side="top"
                align="center"
              >
                <span className="truncate text-12 text-grey-9">{roleName}</span>
              </Tooltip>
            )}
          </div>
        </div>
        <Checkbox
          id={labelId}
          checked={isSelected}
          className="shrink-0"
          disabled={disabled}
          onCheckedChange={handleCheckedChange}
          aria-label={title}
        />
      </label>
    );
  },
);

InviteToLibrariesStepLibraryRow.displayName = 'InviteToLibrariesStepLibraryRow';
